/* src/views/logements/FicheAvisCtrl.jsx — L'ONGLET « Avis » de la fiche
   logement (lot 4, 2026-08-28) : la note moyenne et les moyennes par
   catégorie, les filtres (tous · sur la page · à surveiller), les cartes
   d'avis avec la mise en avant (6 maximum sur la page publique) et la
   feuille « Préparer une réponse » (copilote existant monté tel quel).

   Lecture seule stricte du texte voyageur (ADR-183 / DGCCRF) : seul
   l'ordre d'affichage public se règle, jamais le contenu ni la note.
   DÉFAUT CORRIGÉ (revue lot 4) : la moyenne dit sur QUOI elle est
   calculée quand tout n'est pas chargé — plus de divergence silencieuse
   au-delà de cinquante avis. */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { AVIS_FEATURED_LIMIT, avisFeaturedOverflow } from '../../lib/avis-featured-limit'
import AvisResponseCopilot from '../../tabs/logements/AvisResponseCopilot'
import Feuille from '../accueil/Feuille'

const CATEGORIES_LB = {
  cleanliness: 'Propreté', communication: 'Communication', location: 'Emplacement',
  accuracy: 'Conformité', checkin: 'Arrivée', check_in: 'Arrivée',
  value: 'Rapport qualité-prix', amenities: 'Équipements',
}
const fmtNote = (n) => Number(n).toLocaleString('fr-FR', {
  minimumFractionDigits: 1, maximumFractionDigits: 1 })
const fmtDate = (iso) => {
  if (!iso) return ''
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`)
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(d)
}

export default function FicheAvisCtrl({ draft }) {
  const { selected } = draft
  const log = selected.entity
  const isChambre = selected.isChambre

  const [avis, setAvis] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [erreur, setErreur] = useState(null)
  const [pinError, setPinError] = useState(null)
  const [pinningId, setPinningId] = useState(null)
  const [filtre, setFiltre] = useState('tous')
  const [feuilleAvis, setFeuilleAvis] = useState(null)

  const recharger = useCallback(async () => {
    if (!log.id) return
    setLoading(true); setErreur(null)
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')
      const params = new URLSearchParams({
        action: 'avis_admin_list', logement_id: log.id, status: 'all' })
      const res = await fetch(`/api/sites?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` } })
      const json = await res.json()
      if (!res.ok || !json.ok) throw new Error(json.error || 'fetch_failed')
      setAvis(json.avis || [])
      setTotal(json.total || 0)
    } catch (e) { setErreur(e.message) } finally { setLoading(false) }
  }, [log.id])
  useEffect(() => { setFiltre('tous'); setFeuilleAvis(null); recharger() }, [recharger])

  // Mise en avant — même action, optimiste + rollback (AvisLogementView).
  const basculerPin = useCallback(async (item) => {
    const next = !item.pinned
    setPinError(null); setPinningId(item.id)
    setAvis((prev) => prev.map((a) => (a.id === item.id ? { ...a, pinned: next } : a)))
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')
      const res = await fetch('/api/sites?action=avis_set_pinned', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avis_id: item.id, pinned: next }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.ok) throw new Error(json.error || 'pin_failed')
      setAvis((prev) => prev.map((a) => (a.id === item.id
        ? { ...a, pinned: json.avis?.pinned === true } : a)))
    } catch (e) {
      setAvis((prev) => prev.map((a) => (a.id === item.id
        ? { ...a, pinned: item.pinned } : a)))
      setPinError(e.message)
    } finally { setPinningId(null) }
  }, [])

  const notes = avis.map((a) => Number(a.rating)).filter(Number.isFinite)
  const moyenne = notes.length ? notes.reduce((s, n) => s + n, 0) / notes.length : null
  const parCategorie = useMemo(() => {
    const sommes = {}
    for (const a of avis) {
      for (const [cle, valeur] of Object.entries(a.category_ratings || {})) {
        const n = Number(valeur)
        if (!Number.isFinite(n)) continue
        if (!sommes[cle]) sommes[cle] = { total: 0, n: 0 }
        sommes[cle].total += n; sommes[cle].n += 1
      }
    }
    return Object.entries(sommes).map(([cle, x]) => ({
      lb: CATEGORIES_LB[cle] || cle, note: x.total / x.n }))
  }, [avis])

  const nbPin = avis.filter((a) => a.pinned).length
  const surveilles = avis.filter((a) => Number(a.rating) < 4.5)
  const visibles = filtre === 'page' ? avis.filter((a) => a.pinned)
    : filtre === 'surveiller' ? surveilles : avis
  const debordement = avisFeaturedOverflow(nbPin)

  return (
    <>
      <div className="info">Lecture seule du texte. <b>Seul l'ordre d'affichage
        se règle</b> — la note et le compteur publics ne bougent pas.</div>

      {/* ── la note ───────────────────────────────────────────────── */}
      <div className="carte" style={{ margin: '0 0 9px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px' }}>
          <b style={{ fontSize: '26px', color: 'var(--navy-d)',
            lineHeight: 1 }}>{moyenne != null ? fmtNote(moyenne) : '—'}</b>
          <span style={{ fontSize: '12px', color: 'var(--gris)' }}>sur 5</span>
        </div>
        <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '4px 0 0' }}>
          {total} avis {isChambre ? 'sur cette chambre' : 'sur ce logement'}
          {total > avis.length ? ` — moyenne calculée sur les ${avis.length} chargés` : ''}</p>
        {isChambre && (
          <p style={{ fontSize: '10px', color: 'var(--gris-d)', margin: '3px 0 0' }}>
            la page publique les réunit avec ceux des autres chambres</p>
        )}
        {parCategorie.length > 0 && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap',
            marginTop: '9px', paddingTop: '9px',
            borderTop: '1px solid var(--bord)' }}>
            {parCategorie.map((cat) => (
              <span key={cat.lb} style={{ display: 'inline-flex',
                alignItems: 'baseline', gap: '4px' }}>
                <b style={{ fontSize: '12.5px',
                  color: 'var(--navy-d)' }}>{fmtNote(cat.note)}</b>
                <span style={{ fontSize: '10px',
                  color: 'var(--gris)' }}>{cat.lb}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {debordement > 0 && (
        <div className="warn" data-testid="avis-debordement">La page publique
          n'affiche que <b>{AVIS_FEATURED_LIMIT}</b> avis mis en avant (les mieux
          notés, puis les plus récents) : {debordement} épinglé{debordement > 1 ? 's' : ''} n'appara{debordement > 1 ? 'issent' : 'ît'} pas.
          Retirez-en pour choisir lesquels sortent.</div>
      )}
      {erreur && (
        <div className="info" style={{ borderColor: '#e8c4b8',
          background: '#fbeee9', color: '#8a3b28' }}>{erreur}</div>
      )}
      {pinError && (
        <div className="info" data-testid="avis-pin-erreur"
          style={{ borderColor: '#e8c4b8', background: '#fbeee9',
            color: '#8a3b28' }}>Mise en avant échouée : {pinError}</div>
      )}

      {/* ── les filtres ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center',
        marginBottom: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', color: 'var(--gris)' }}>Voir</span>
        {[{ id: 'tous', lb: 'tous', n: avis.length },
          { id: 'page', lb: 'sur la page', n: nbPin },
          { id: 'surveiller', lb: 'à surveiller', n: surveilles.length }].map((f) => (
          <button key={f.id} onClick={() => setFiltre(f.id)}
            data-testid={`avis-filtre-${f.id}`}
            style={filtre === f.id
              ? { minHeight: '32px', padding: '0 11px', borderRadius: '9px',
                fontSize: '10.5px', background: 'var(--ciel-p)',
                border: '1px solid #87b8e6', color: 'var(--navy-d)',
                fontWeight: 600, cursor: 'pointer' }
              : { minHeight: '32px', padding: '0 11px', borderRadius: '9px',
                fontSize: '10.5px', background: '#fff', border: '1px solid #d5e3ee',
                color: 'var(--gris)', cursor: 'pointer' }}>
            {f.lb}<span style={{ marginLeft: '5px', fontSize: '9.5px',
              color: 'var(--gris-d)' }}>{f.n}</span></button>
        ))}
        <button onClick={recharger} disabled={loading}
          aria-label="Actualiser les avis"
          style={{ minHeight: '32px', padding: '0 10px', borderRadius: '9px',
            border: '1px solid #d5e3ee', background: '#fff', fontSize: '11px',
            color: 'var(--navy)', cursor: loading ? 'wait' : 'pointer',
            marginLeft: 'auto' }}>↻</button>
      </div>

      {loading && (
        <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Chargement…</p>
      )}
      {!loading && visibles.length === 0 && (
        <div className="carte"><p style={{ fontSize: '11.5px',
          color: 'var(--gris)', margin: 0 }}>
          {avis.length === 0 ? 'Aucun avis pour ce logement.' : 'Rien avec ce filtre.'}</p></div>
      )}

      {/* ── les cartes d'avis ─────────────────────────────────────── */}
      {visibles.map((a) => {
        const affichable = a.status === 'auto_approved'
        return (
          <div className="carte" key={a.id} style={{ margin: '0 0 8px' }}
            data-testid={`avis-carte-${a.id}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
              flexWrap: 'wrap', marginBottom: '7px' }}>
              <b style={{ fontSize: '15px',
                color: 'var(--navy-d)' }}>{fmtNote(a.rating)}</b>
              <span style={{ fontSize: '10.5px',
                color: 'var(--gris)' }}>{fmtDate(a.review_date)}</span>
              <span className="tag" style={{ background: a.source === 'booking'
                ? '#e8eefc' : '#fdeaf0', color: a.source === 'booking'
                ? '#1a4fa0' : '#c02b55' }}>{a.source === 'booking' ? 'Booking' : 'Airbnb'}</span>
              {a.status === 'pending' && (
                <span className="tag" style={{ background: 'var(--ambre-bg)',
                  color: 'var(--ambre-tx)' }}>en attente</span>
              )}
              {['rejected', 'hidden'].includes(a.status) && (
                <span className="tag" style={{ background: '#eef2f6',
                  color: 'var(--gris)' }}>{a.status === 'rejected' ? 'rejeté' : 'masqué'}</span>
              )}
              {affichable && (
                <span style={{ fontSize: '10.5px', marginLeft: 'auto',
                  color: a.pinned ? 'var(--terra-tx)' : 'var(--gris-d)' }}>
                  {a.pinned ? '★ sur la page' : '☆'}</span>
              )}
            </div>
            <p style={{ fontSize: '11.5px', color: 'var(--navy-d)', margin: '0',
              lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{a.review_text}</p>
            {a.category_ratings && Object.keys(a.category_ratings).length > 0 && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap',
                marginTop: '8px' }}>
                {Object.entries(a.category_ratings)
                  .filter(([, v]) => Number.isFinite(Number(v)))
                  .map(([cle, v]) => (
                    <span key={cle} style={{ fontSize: '9.5px',
                      color: 'var(--gris)' }}>{CATEGORIES_LB[cle] || cle}{' '}
                      <b style={{ color: 'var(--navy-d)' }}>{v}</b></span>
                  ))}
              </div>
            )}
            <div className="act" style={{ marginTop: '9px' }}>
              {affichable && (
                <button className="bt bt-s" disabled={pinningId === a.id}
                  data-testid={`avis-pin-${a.id}`} aria-pressed={!!a.pinned}
                  onClick={() => basculerPin(a)}>
                  {a.pinned ? '★ Retirer de la page' : '☆ Mettre sur la page'}</button>
              )}
              <button className="bt bt-s" data-testid={`avis-repondre-${a.id}`}
                onClick={() => setFeuilleAvis(a)}>Préparer une réponse</button>
            </div>
          </div>
        )
      })}
      <p className="note">La loi interdit de trier ou de masquer les avis. Ce
        que vous réglez ici est l'ordre, jamais le contenu.</p>

      {feuilleAvis && (
        <Feuille titre="Répondre à cet avis" testId="feuille-reponse-avis"
          onFermer={() => setFeuilleAvis(null)}
          enfants={(
            <>
              <div className="carte" style={{ margin: '0 0 9px' }}>
                <p style={{ fontSize: '11.5px', color: 'var(--navy-d)', margin: 0,
                  lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{feuilleAvis.review_text}</p>
                <p className="note">{fmtDate(feuilleAvis.review_date)} ·{' '}
                  {fmtNote(feuilleAvis.rating)} sur 5</p>
              </div>
              {/* Copilote existant monté tel quel : générer / régénérer /
                  éditer / copier / lien Airbnb — brouillon éphémère, rien
                  n'est enregistré ni publié. */}
              <AvisResponseCopilot avis={feuilleAvis} />
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuilleAvis(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
    </>
  )
}
